"use client";
import { AutomationFeature, AutomationsProps } from "@/shared/data/home.v2.data";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import AutomationAccordionItem from "@/components/Home/v2/shared/AutomationAccordionItem";
import SectionHeader from "@/components/Home/v2/shared/SectionHeader";
import { ScrollAnimatedSplitHeadingSectionData } from "./ScrollAnimatedSplitHeadingSection";

/**
 * ShipFasterWithAccordionSection — merges the scroll-animated heading
 * ("Ship faster") and the feature accordion into a single <section> so
 * there is no visible boundary between the two blocks.
 *
 * The heading area sits on the lavender → white gradient; the accordion
 * sits on the plain white that the gradient fades into, so the two
 * visually flow together.
 */

const AUTO_ADVANCE_MS = 5000;

// Gradient spans the full section (heading + accordion). The linear layer
// fades lavender → white over ~65% of the total height so the colour is
// visible well into the accordion area. The radial blobs sit at the top
// corners and are sized relative to the viewport so they stay large even
// when the section is tall.
const BG = [
  "linear-gradient(180deg, rgba(220,214,252,0.9) 0%, rgba(232,228,253,0.72) 25%, rgba(232,228,253,0.35) 52%, rgba(232,228,253,0.08) 74%, #FFFFFF 92%)",
  "radial-gradient(ellipse 100% 62% at 0% 0%, rgba(167,139,250,0.42) 0%, rgba(167,139,250,0.1) 58%, transparent 82%)",
  "radial-gradient(ellipse 100% 62% at 100% 0%, rgba(139,130,255,0.36) 0%, rgba(139,130,255,0.08) 58%, transparent 82%)",
].join(", ");

const FeatureImageSlot = ({
  image,
  imageMobile,
  alt,
}: {
  image?: string;
  imageMobile?: string;
  alt: string;
}) => (
  <div className="mx-auto w-full overflow-hidden rounded-[32px] bg-gradient-to-br from-[#EEF1FF] via-[#E4E8FF] to-[#D4D9FF] shadow-[0_24px_60px_-24px_rgba(35,34,40,0.18)]">
    {image ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={image}
        alt={alt}
        className={
          imageMobile
            ? "hidden w-full h-auto md:block"
            : "w-full h-auto"
        }
      />
    ) : null}
    {imageMobile ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={imageMobile}
        alt={alt}
        className={
          image
            ? "block w-full h-auto md:hidden"
            : "w-full h-auto"
        }
      />
    ) : null}
    {!image && !imageMobile ? (
      <div className="min-h-[340px] md:min-h-[520px]">
        <div className="absolute inset-6 rounded-2xl bg-white/40 backdrop-blur-sm" />
      </div>
    ) : null}
  </div>
);

export type ShipFasterWithAccordionData = ScrollAnimatedSplitHeadingSectionData & AutomationsProps;

const ShipFasterWithAccordionSection = ({
  // Heading props
  staticText,
  animatedText,
  animatedGradient = "linear-gradient(to right, #5B50F5, #8B82FF, #BDB8FF)",
  // Accordion props
  eyebrow,
  title,
  subtitle,
  features,
  imagePosition = "right",
  ctas,
}: ShipFasterWithAccordionData) => {
  // ── Scroll animation (heading) ──────────────────────────────────────────
  const sectionRef = useRef<HTMLDivElement>(null);
  const [revealPct, setRevealPct] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // 0 when section enters from bottom, 1 when section top reaches viewport top
      const progress = 1 - rect.top / window.innerHeight;
      setRevealPct(Math.round(Math.min(100, Math.max(0, progress * 100))));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Accordion auto-advance ───────────────────────────────────────────────
  const [activeIdx, setActiveIdx] = useState(0);
  const [progressKey, setProgressKey] = useState(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!features.length) return;
    timerRef.current = setTimeout(() => {
      setActiveIdx((i) => (i + 1) % features.length);
      setProgressKey((k) => k + 1);
    }, AUTO_ADVANCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeIdx, features.length]);

  const handleToggle = (id: string) => {
    const nextIdx = features.findIndex((f) => f.id === id);
    if (nextIdx === -1) return;
    setActiveIdx(nextIdx);
    setProgressKey((k) => k + 1);
  };

  const activeFeature: AutomationFeature | undefined = features[activeIdx];

  const fontSize = "clamp(60px, 15vw, 220px)";

  const headingText = (dimColor: string, gradient: string | null) => (
    <span className="font-inter" style={{ lineHeight: 1.05, display: "block" }}>
      <span style={{ fontSize, fontWeight: 600, letterSpacing: "-0.04em", color: dimColor }}>
        {staticText}
      </span>
      <span
        style={{
          fontSize,
          fontWeight: 700,
          letterSpacing: "-0.04em",
          marginLeft: "0.1em",
          ...(gradient
            ? {
                backgroundImage: gradient,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                WebkitTextFillColor: "transparent",
              }
            : { color: dimColor }),
        }}
      >
        {animatedText}
      </span>
    </span>
  );

  const accordion = (
    <div>
      {features.map((feature, idx) => (
        <AutomationAccordionItem
          key={feature.id}
          id={feature.id}
          title={feature.title}
          description={feature.description}
          bullets={feature.bullets}
          bulletsHeading={feature.bulletsHeading}
          footerLine={feature.footerLine}
          isOpen={idx === activeIdx}
          onToggle={handleToggle}
          isProgressActive={idx === activeIdx}
          progressKey={progressKey}
          showDivider
        />
      ))}
    </div>
  );

  const media = (
    <FeatureImageSlot
      image={activeFeature?.image}
      imageMobile={activeFeature?.imageMobile}
      alt={activeFeature?.title ?? ""}
    />
  );

  return (
    // Gradient applied to the full section so it washes evenly over both
    // the heading and the accordion below — not squeezed into a tiny band.
    <section ref={sectionRef} className="w-full" style={{ background: BG }}>
      {/* ── Scroll-animated heading ── */}
      <div
        className="w-full overflow-hidden flex items-center justify-center"
        style={{ paddingTop: "10vh", paddingBottom: "2vh" }}
      >
        <div className="relative w-full overflow-hidden px-8 md:px-16 text-center">
          {/* Dim base */}
          <div>{headingText("rgba(139,130,255,0.18)", null)}</div>

          {/* Bright revealed layer — scroll-driven */}
          <div
            className="absolute inset-0 flex items-center justify-center px-8 md:px-16"
            style={{ clipPath: `inset(0 ${100 - revealPct}% 0 0)` }}
          >
            {headingText("#9CA3AF", animatedGradient)}
          </div>
        </div>
      </div>

      {/* ── Accordion ── */}
      <div className="container mx-auto px-4 pt-4 pb-10 sm:px-6 md:px-8 md:pt-6 md:pb-20 lg:px-12 lg:pt-8 lg:pb-24">
        {title && (
          <SectionHeader
            eyebrow={eyebrow}
            titleMuted={title.includes("\n") ? title.split("\n")[0] : undefined}
            titleBright={title.includes("\n") ? title.split("\n")[1] : undefined}
            title={title.includes("\n") ? undefined : title}
            subtitle={subtitle}
            variant="light-teal"
            align="center"
            ctas={ctas}
          />
        )}

        <div
          className={classNames(
            "mx-auto mt-12 grid max-w-6xl gap-10 md:mt-16 md:items-center md:gap-16",
            imagePosition === "left"
              ? "md:grid-cols-[13fr_7fr]"
              : "md:grid-cols-[7fr_13fr]"
          )}
        >
          {imagePosition === "left" ? (
            <>
              {media}
              {accordion}
            </>
          ) : (
            <>
              {accordion}
              {media}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ShipFasterWithAccordionSection;
